/**
 * Keyboard operation of modal dialogs (PROJECT_SPEC.md §10.5, URS-10.5-004): focus moves into the dialog when it opens,
 * Tab and Shift+Tab stay inside it while it is open, and focus returns to what had it when it closes. Without this a
 * keyboard user tabs out of the signature dialog into the page behind it and cannot see where focus went.
 */
import { type RefObject, useEffect } from "react";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function focusables(container: HTMLElement): HTMLElement[] {
  return [...container.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => !el.hidden);
}

/** Wraps Tab / Shift+Tab at the ends of the dialog. Returns true when it handled the event. */
export function trapTab(container: HTMLElement, e: KeyboardEvent, active: Element | null): boolean {
  if (e.key !== "Tab") return false;
  const items = focusables(container);
  const first = items[0];
  const last = items[items.length - 1];
  if (!first || !last) {
    e.preventDefault();
    container.focus();
    return true;
  }
  const outside = !active || !container.contains(active);
  if (e.shiftKey && (active === first || outside || active === container)) {
    e.preventDefault();
    last.focus();
    return true;
  }
  if (!e.shiftKey && (active === last || outside)) {
    e.preventDefault();
    first.focus();
    return true;
  }
  return false;
}

export function useDialogFocus(ref: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const before = document.activeElement as HTMLElement | null;
    (el.querySelector<HTMLElement>("[data-autofocus]") ?? focusables(el)[0] ?? el).focus();
    const onKey = (e: KeyboardEvent): void => {
      trapTab(el, e, document.activeElement);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      before?.focus?.();
    };
  }, [ref]);
}
